import React, { useState, useEffect, useRef, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import OtpInput from "react18-input-otp";
import { Player } from "@lottiefiles/react-lottie-player";
import { KeyIcon, XCircleIcon } from "@heroicons/react/24/outline";
import loaderAnim from "../../assets/icons/otp.json";
import BreadCrumb from "../BreadCrumb";
import { getConfiguration } from "../../../features/Global/Configuration";
import {
  OTP_LENGTH,
  OTP_TTL_SECONDS,
  normalizePhone,
  requestOtp,
  verifyOtp,
  errorKeyFromCode,
  successKeyFromCode,
} from "../../services/passwordResetApi";

const formatTime = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
};

function MyApp() {
  const locale = localStorage.getItem("i18nextLng");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = useSelector((state) => state.Configuration?.data);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(OTP_TTL_SECONDS);
  const [phoneError, setPhoneError] = useState("");
  const [otpFeedback, setOtpFeedback] = useState(null); // { key, isError }
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const timerRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    dispatch(getConfiguration());
  }, [dispatch]);

  const stopTimer = () => clearInterval(timerRef.current);

  const startTimer = () => {
    stopTimer();
    setSecondsLeft(OTP_TTL_SECONDS);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          stopTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => stopTimer, []);

  const inputStyles = {
    border: "1px solid #2f3e46",
    borderRadius: "8px",
    width: window.innerWidth <= 600 ? "40px" : "54px",
    height: window.innerWidth <= 600 ? "40px" : "54px",
    fontSize: "16px",
    color: "#2f3e46",
    fontWeight: "400",
    caretColor: "pink",
    direction: "ltr",
    unicodeBidi: "bidi-override",
  };

  // Step 1: request the OTP, then open the OTP screen.
  const onSubmit = async (form) => {
    const phone = normalizePhone(form.phoneNumber);
    setPhoneError("");
    setSendingOtp(true);
    const res = await requestOtp(phone);
    setSendingOtp(false);
    if (res.ok && res.code === "OTP_SENT") {
      setPhoneNumber(phone);
      setOtp("");
      setOtpFeedback(null);
      setShowModal(true);
      startTimer();
    } else {
      setPhoneError(errorKeyFromCode(res.code));
    }
  };

  const onResend = async () => {
    setResending(true);
    setOtpFeedback(null);
    const res = await requestOtp(phoneNumber);
    setResending(false);
    if (res.ok && res.code === "OTP_SENT") {
      setOtp("");
      startTimer();
      setOtpFeedback({ key: successKeyFromCode(res.code), isError: false });
    } else {
      setOtpFeedback({ key: errorKeyFromCode(res.code), isError: true });
    }
  };

  // Step 2: validate the code (does not reset the password); only a 200
  // OTP_VALID response may reach the new-password screen.
  const onVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH || verifying) return;
    setVerifying(true);
    setOtpFeedback(null);
    const res = await verifyOtp(phoneNumber, otp);
    setVerifying(false);
    if (res.ok && res.code === "OTP_VALID") {
      stopTimer();
      navigate("/change-password", {
        state: { phoneNumber, otpCode: otp },
        replace: true,
      });
    } else {
      setOtpFeedback({ key: errorKeyFromCode(res.code), isError: true });
    }
  };

  const orgName =
    (locale === "en-US" && data[0]?.englishName) ||
    (locale === "ar" && data[0]?.arabicName) ||
    (locale === "ku" && data[0]?.kurdishName) ||
    "";

  return (
    <div className="container p-4 mt-4 mb-8 rounded-lg bg-gray-50">
      <BreadCrumb Name={"ressetPassword"} Iconn={KeyIcon} className="m-3" />
      <div className="flex flex-wrap items-center mx-auto ">
        <div className="w-full px-4 py-10 mx-3 text-white lg:w-3/4 lg:mx-2 lg:max-w-xl rounded-3xl bg-gradient-to-t from-pink-700 via-pink-700 to-pink-600 sm:px-10 md:m-6 md:mr-8">
          <p
            className={`mb-10 font-bold tracking-wider text-center text-lg ${locale}`}
          >
            <img
              src={data[0]?.imageUrl}
              alt=""
              className="inline object-cover w-16 h-16 mx-2 rounded-full"
            />{" "}
            {orgName}{" "}
          </p>
          <p
            className={`mb-4 text-3xl font-bold md:text-4xl md:leading-snug ${locale}`}
          >
            {t("resetPageMessage")}
          </p>
          <p className={`mb-28 leading-relaxed text-gray-200 ${locale}`}>
            {t("resetPageMessageDetail")}
          </p>
          <div className="px-4 py-8 bg-pink-600/80 rounded-2xl">
            <div className="flex items-center">
              <p className={`${locale === "en-US" ? "ml-4" : "mr-4"} w-56`}>
                {locale !== "en-US" && (
                  <strong className={`block font-medium ${locale}`}>
                    {orgName}{" "}
                  </strong>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-col w-full mx-4 my-3 bg-white rounded-lg shadow-2xl lg:mx-auto lg:flex lg:w-2/5">
          <div className="mx-2 my-6 text-center">
            <h1 className={`text-3xl font-semibold text-gray-800 ${locale}`}>
              {t("ressetPassword")}
            </h1>
            <p className={`mt-2 font-medium text-gray-700 ${locale}`}>
              {t("dontHaveAccount")}{" "}
              <Link
                to="/register"
                className={`text-pink-500 cursor-pointer hover:text-pink-700 ${locale}`}
              >
                {t("createAccount")}
              </Link>
            </p>
          </div>
          <div className="pb-8 mx-6">
            <form className="mb-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-6">
                <label
                  htmlFor="phoneNumber"
                  className={`block mb-2 text-sm text-gray-600 ${locale}`}
                >
                  {t("phoneNumberPlaceholder")}
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  placeholder={t("770(50)000 00 00")}
                  style={{ direction: "ltr" }}
                  {...register("phoneNumber", {
                    required: true,
                    pattern: /[0-9]{1}/,
                  })}
                  className={`w-full px-3 py-3 placeholder-gray-300 border border-gray-300 rounded-xl focus:outline-none focus:ring focus:ring-pink-100 focus:border-pink-600 ${
                    errors.phoneNumber &&
                    "focus:ring-rose-100 focus:border-rose-300"
                  } ${locale}`}
                />
                {phoneError && (
                  <p
                    role="alert"
                    className={`mt-2 text-sm text-rose-600 ${locale}`}
                  >
                    {t(phoneError)}
                  </p>
                )}
              </div>
              <div className="mb-6">
                <button
                  type="submit"
                  disabled={sendingOtp}
                  className={`w-full px-3 py-3 text-lg font-medium text-white duration-100 ease-in-out bg-pink-600 rounded-xl hover:bg-pink-400 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${locale}`}
                >
                  {t("reset")}
                </button>
              </div>
            </form>

            <Transition appear show={showModal} as={Fragment}>
              <Dialog as="div" className="relative z-10 " onClose={() => {}}>
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-full p-4 text-center">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-300"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="ease-in duration-200"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <Dialog.Panel className="w-full max-w-xl overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl md:p-6 rounded-2xl">
                        <div className="relative flex flex-col justify-center py-8 overflow-hidden ">
                          <div className="relative w-full max-w-xl pt-10 mx-auto bg-white shadow-xl md:px-6 pb-9 rounded-2xl">
                            <button
                              type="button"
                              aria-label={t("close")}
                              onClick={() => {
                                stopTimer();
                                setShowModal(false);
                              }}
                              className="fixed top-0 right-0 float-right"
                            >
                              <XCircleIcon className="inline w-8 p-1 duration-500 rounded-full hover:text-white text-rose-600 hover:bg-rose-600" />
                            </button>
                            <div className="flex flex-col w-full max-w-3xl mx-auto space-y-10">
                              <div className="flex flex-col items-center justify-center space-y-2 text-center">
                                <div className="text-3xl font-semibold">
                                  <p className={locale}>
                                    {t("accountVerification")}
                                  </p>
                                </div>
                                <div className="flex flex-row text-sm font-medium text-gray-400">
                                  <p className={locale}>
                                    {t("weHaveSendMessageForYourPhoneNumber")}{" "}
                                  </p>
                                </div>
                                <Player
                                  src={loaderAnim}
                                  autoplay
                                  loop
                                  className="w-56 player"
                                />
                              </div>
                              <form onSubmit={onVerify}>
                                <div className="flex flex-col space-y-8">
                                  <div className="flex flex-row items-center justify-between w-full mx-auto max-w-7xl">
                                    <span className="w-full mx-auto">
                                      <OtpInput
                                        value={otp}
                                        onChange={setOtp}
                                        numInputs={OTP_LENGTH}
                                        separator={
                                          <span style={{ width: "8px" }}></span>
                                        }
                                        isInputNum={true}
                                        shouldAutoFocus={true}
                                        className="mx-1 md:mx-2"
                                        inputStyle={inputStyles}
                                        focusStyle={{
                                          border: "1px solid #CFD3DB",
                                          outline: "none",
                                        }}
                                      />
                                    </span>
                                  </div>

                                  <div
                                    className="text-center"
                                    aria-live="polite"
                                  >
                                    {secondsLeft > 0 ? (
                                      <span
                                        className="font-mono text-gray-500"
                                        style={{ direction: "ltr" }}
                                      >
                                        {formatTime(secondsLeft)}
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={onResend}
                                        disabled={resending}
                                        className={`text-sm font-medium text-pink-600 hover:text-pink-800 disabled:opacity-60 ${locale}`}
                                      >
                                        {t("resendCode")}
                                      </button>
                                    )}
                                  </div>

                                  {otpFeedback && (
                                    <p
                                      role="alert"
                                      className={`text-sm text-center ${
                                        otpFeedback.isError
                                          ? "text-rose-600"
                                          : "text-green-600"
                                      } ${locale}`}
                                    >
                                      {t(otpFeedback.key)}
                                    </p>
                                  )}

                                  <div className="flex items-center justify-center ">
                                    <button
                                      type="submit"
                                      id="submit"
                                      disabled={
                                        verifying || otp.length !== OTP_LENGTH
                                      }
                                      className={`flex flex-row items-center justify-center w-4/5 md:w-full mx-1 py-5 text-sm text-center text-white bg-pink-600 border border-none shadow-sm outline-none rounded-xl disabled:opacity-60 disabled:cursor-not-allowed ${locale}`}
                                    >
                                      {t("verifyAccount")}
                                    </button>
                                  </div>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Dialog>
            </Transition>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyApp;
